// In src/PropertiesPanel.jsx
import { Copy } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as THREE from 'three';

// Reusable component for the grid-based property display
const PropertyGrid = ({ items, columns = 1 }) => {
    const handleCopy = (value) => {
        navigator.clipboard.writeText(value).catch(err => console.error("Failed to copy:", err));
    };

    return (
        <div className="prop-grid" style={{ '--columns': columns }}>
            {items.map((item, index) => (
                item ? (
                    <div key={index} className="prop-grid-item">
                        <div className="prop-grid-label">{item.label}</div>
                        <div className="prop-grid-value">{item.value}</div>
                        {/* {item.copyValue && (
                            <button className="prop-copy-btn" onClick={() => handleCopy(item.copyValue)} title="Copy">
                                <Copy size={14} />
                            </button>
                        )} */}
                    </div>
                ) : null
            ))}
        </div>
    );
};

const PropertySummary = ({ label, value }) => (
    <div className="prop-summary">
        <span className="prop-summary-label">{label}:</span>
        <span className="prop-summary-value">{value}</span>
    </div>
);

const NumberInput = ({ label, value, onChange, step = 0.1 }) => {
    // We still use an internal state to allow the user to type freely (e.g., "1." or "-")
    const [inputValue, setInputValue] = useState(String(value));

    // This effect syncs the input if the value is changed from outside (e.g., by dragging a point)
    useEffect(() => {
        // Update the input's displayed value only if it's different from the prop,
        // and only if the user is not currently focused on the input. This prevents the
        // cursor from jumping while typing.
        if (document.activeElement !== document.getElementById(`num_input_${label}`)) {
            setInputValue(value.toFixed(2));
        }
    }, [value, label]);

    const handleInputChange = (e) => {
        const currentInput = e.target.value;
        setInputValue(currentInput); // Immediately update what the user sees in the box

        // Try to parse the input as a number
        const numericValue = parseFloat(currentInput);

        if (!isNaN(numericValue)) {
            onChange(numericValue);
        }
    };

    const handleBlur = () => {
        // On blur (clicking away), if the user left invalid text (e.g., "abc"),
        // snap the input back to the last known valid value from the parent.
        if (isNaN(parseFloat(inputValue))) {
            setInputValue(value.toFixed(2));
        }
    };

    return (
        <div className="prop-summary">
            <span className="prop-summary-label">{label}:</span>
            <input
                id={`num_input_${label}`} // Unique ID for focus check
                type="number"
                className="prop-number-input"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleBlur} // Keep blur for validation
                step={step}
            />
        </div>
    );
};

const SelectControl = ({ label, value, onChange, options }) => (
    <div className="option-row">
        <label>{label}</label>
        <select className="prop-select" value={value} onChange={e => onChange(e.target.value)}>
            {options.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
        </select>
    </div>
);

const SliderControl = ({ label, value, onChange, min, max, step, isRange = false }) => {
    // This is a special case for two-value sliders like elevation range
    if (isRange) {
        // This requires a more complex slider library like 'react-slider' or 'rc-slider'
        // For now, we'll just display the values as a placeholder.
        // To implement this, you would: npm install react-slider, then import and use it here.
        return (
            <div className="option-row">
                <label>{label}: <span className="option-value">{value}</span></label>
                <div className="slider-placeholder">Range slider requires a dedicated library</div>
            </div>
        );
    }

    // Standard single-value slider
    const displayValue = typeof value === 'number' ? value : '...';
    return (
        <div className="option-row">
            <label>{label}: <span className="option-value">{displayValue}</span></label>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={typeof value === 'number' ? value : min}
                onChange={e => onChange(parseFloat(e.target.value))}
                className="slider"
            />
        </div>
    );
};

// --- Specific Property Components (Now they just prepare data) ---

const PointCloudProperties = ({ pointcloud, t }) => {
    const mat = pointcloud.material;
    const [attribute, setAttribute] = useState(mat.activeAttributeName || attributes[0]);
    const [size, setSize] = useState(mat.size);
    const [gamma, setGamma] = useState(mat.gamma);
    const [brightness, setBrightness] = useState(mat.brightness);
    const [contrast, setContrast] = useState(mat.contrast);

    // --- Build the attribute list the Potree way ---
    let baseAttributes = pointcloud.pcoGeometry.pointAttributes.attributes.map(a => a.name);

    // Insert "intensity gradient" after "intensity"
    const intensityIndex = baseAttributes.indexOf("intensity");
    if (intensityIndex >= 0) {
        baseAttributes.splice(intensityIndex + 1, 0, "intensity gradient");
    }

    const extraAttributes = [
        "elevation",
    ];

    const blacklist = ["POSITION_CARTESIAN", "position", "NORMAL_SPHEREMAPPED"];
    const attributes = [...baseAttributes, ...extraAttributes].filter(
        a => !blacklist.includes(a)
    );
    // --- Keep UI in sync with Potree ---
    useEffect(() => {
        const onChanged = () => {
            setAttribute(mat.activeAttributeName);
        };
        mat.addEventListener("point_color_type_changed", onChanged);
        mat.addEventListener("active_attribute_changed", onChanged);
        return () => {
            mat.removeEventListener("point_color_type_changed", onChanged);
            mat.removeEventListener("active_attribute_changed", onChanged);
        };
    }, [mat]);

    const handleSize = (v) => {
        setSize(v);
        mat.size = v;
        mat.needsUpdate = true;
    };

    // --- Handlers ---
    const handleAttribute = (value) => {
        setAttribute(value);
        mat.activeAttributeName = value;
        mat.needsUpdate = true;

        let attr = pointcloud.getAttribute(value);
        if (value === "intensity gradient") {
            attr = pointcloud.getAttribute("intensity");
        }
    };

    // const handleGamma = (v) => {
    //     setGamma(v);
    //     mat.gamma = v;
    //     mat.needsUpdate = true;
    // };

    // const handleBrightness = (v) => {
    //     setBrightness(v);
    //     mat.brightness = v;
    //     mat.needsUpdate = true;
    // };

    // const handleContrast = (v) => {
    //     setContrast(v);
    //     mat.contrast = v;
    //     mat.needsUpdate = true;
    // };


    return (
        <div className="prop-list-vertical">
            <SliderControl
                label={t("scenePanel.properties.labels.pointSize")}
                value={size}
                onChange={handleSize}
                min={0.01}
                max={3}
                step={0.01}
                formatter={(v) => v.toFixed(2)}
            />

            <SelectControl
                label={t("scenePanel.properties.labels.attribute")}
                value={attribute}
                onChange={handleAttribute}
                options={attributes.map(a => ({ value: a, label: a }))}
            />

            {/* {attribute === "rgba" && (
                <div className="rgb-controls">
                    <SliderControl
                        label="Gamma"
                        value={gamma}
                        onChange={handleGamma}
                        min={0}
                        max={4}
                        step={0.01}
                        formatter={(v) => v.toFixed(2)}
                    />
                    <SliderControl
                        label="Brightness"
                        value={brightness}
                        onChange={handleBrightness}
                        min={-1}
                        max={1}
                        step={0.01}
                        formatter={(v) => v.toFixed(2)}
                    />
                    <SliderControl
                        label="Contrast"
                        value={contrast}
                        onChange={handleContrast}
                        min={-1}
                        max={1}
                        step={0.01}
                        formatter={(v) => v.toFixed(2)}
                    />
                </div>
            )} */}
        </div>
    );
};


const MeasureProperties = ({ measure, t }) => {
    const forceUpdate = React.useReducer(x => x + 1, 0)[1];

    useEffect(() => {
        const onUpdate = () => forceUpdate();
        measure.addEventListener('marker_moved', onUpdate);
        measure.addEventListener('radius_changed', onUpdate);
        return () => {
            measure.removeEventListener('marker_moved', onUpdate);
            measure.removeEventListener('radius_changed', onUpdate);
        };
    }, [measure]);

    // We now have two separate arrays for our data
    let gridItems = [];
    let summaryItems = [];
    const name = measure.name;

    // --- Populate Grid Items (Coordinates & Angles) ---
    if (name === 'Point Measurement') {
        const p = measure.points[0].position;
        gridItems.push({ label: "x", value: p.x.toFixed(3), copyValue: `X:${p.x.toFixed(3)}, Y:${p.y.toFixed(3)}, Z:${p.z.toFixed(3)}` });
        gridItems.push({ label: "y", value: p.y.toFixed(3) });
        gridItems.push({ label: "z", value: p.z.toFixed(3) });
    } else {
        measure.points.forEach((p, i) => {
            gridItems.push({ label: `P${i + 1} x`, value: p.position.x.toFixed(3), copyValue: `X:${p.position.x.toFixed(3)}, Y:${p.position.y.toFixed(3)}, Z:${p.position.z.toFixed(3)}` });
            gridItems.push({ label: `P${i + 1} y`, value: p.position.y.toFixed(3) });
            gridItems.push({ label: `P${i + 1} z`, value: p.position.z.toFixed(3) });
        });
    }

    if (measure.showAngles) {
        const angles = (measure.angleLabels || []).map(a => a.text);
        while (angles.length < 3) { angles.push('---'); }
        gridItems.push({ label: "α", value: angles[0] });
        gridItems.push({ label: "β", value: angles[1] });
        gridItems.push({ label: "γ", value: angles[2] });
    }

    // --- Populate Summary Items (Single Values) ---
    if (measure.showDistances) {
        summaryItems.push({ label: t("scenePanel.properties.labels.length"), value: measure.getTotalDistance().toFixed(2) + ' m' });
    }
    if (measure.showHeight && measure.points.length === 2) {
        const height = Math.abs(measure.points[0].position.z - measure.points[1].position.z);
        summaryItems.push({ label: t("scenePanel.properties.labels.height"), value: height.toFixed(2) + ' m' });
    }
    if (measure.showArea) {
        summaryItems.push({ label: t("scenePanel.properties.labels.area"), value: measure.getArea().toFixed(2) + ' m²' });
    }
    if (measure.showCircle) {
        const radiusText = measure.circleRadiusLabel.text;
        const radiusValue = parseFloat(radiusText.replace(/[^0-9.-]+/g, ""));
        const circumference = 2 * Math.PI * radiusValue;
        const center = measure.circleCenter.position;

        // Add Center coords to the grid
        gridItems.push(null, null, null); // Divider
        gridItems.push({ label: t("scenePanel.properties.labels.centerX"), value: center.x.toFixed(3), copyValue: `X:${center.x.toFixed(3)}, Y:${center.y.toFixed(3)}, Z:${center.z.toFixed(3)}` });
        gridItems.push({ label: t("scenePanel.properties.labels.centerY"), value: center.y.toFixed(3) });
        gridItems.push({ label: t("scenePanel.properties.labels.centerZ"), value: center.z.toFixed(3) });

        // Add Radius and Circumference to the summary
        summaryItems.push({ label: t("scenePanel.properties.labels.radius"), value: radiusText });
        summaryItems.push({ label: t("scenePanel.properties.labels.circumference"), value: circumference.toFixed(2) + ' m' });
    }

    return (
        <>
            {/* Conditionally render the grid only if it has items */}
            {gridItems.length > 0 && <PropertyGrid items={gridItems} columns={3} />}

            {/* Render the list of summary items */}
            {summaryItems.length > 0 && (
                <>
                    {summaryItems.map(item => (
                        <PropertySummary key={item.label} label={item.label} value={item.value} />
                    ))}
                </>
            )}
        </>
    );
};



const VolumeProperties = ({ volume, t }) => {
    const [clip, setClip] = React.useState(volume.clip);
    const forceUpdate = React.useReducer(x => x + 1, 0)[1];
    console.log('Volume: ', volume);

    // --- FIX #1: Live-updating useEffect ---
    useEffect(() => {
        const onVolumeTransformed = () => forceUpdate();
        
        
        // These events work correctly for position and scale, so we keep them.
        volume.addEventListener('position_changed', onVolumeTransformed);
        volume.addEventListener('scale_changed', onVolumeTransformed);

        // --- FIX: Add polling for rotation updates ---
        // Since no continuous rotation event exists, we check manually at a high frequency.
        const rotationInterval = setInterval(() => {
            // This is a lightweight check and update, running every ~60fps
            forceUpdate();
        }, 100); // ~10 times per second

        const clipSyncInterval = setInterval(() => {
            if (volume.clip !== clip) {
                setClip(volume.clip);
            }
        }, 200); 

        // CRUCIAL: Cleanup all listeners AND the interval
        return () => {
            volume.removeEventListener('position_changed', onVolumeTransformed);
            volume.removeEventListener('scale_changed', onVolumeTransformed);
            // Cleanup the interval to prevent memory leaks when the component unmounts
            clearInterval(rotationInterval);
            clearInterval(clipSyncInterval);
        };
    }, [volume, clip]);


    const handleClipChange = (e) => {
        volume.clip = e.target.checked;
        setClip(e.target.checked);
    };

    // --- FIX #2: Implement the logic directly ---
    const handleReset = () => {
        volume.rotation.set(0, 0, 0); // Set rotation euler to zero
        forceUpdate(); // Re-render to show the new values
    };

    // --- FIX #3: Implement the logic directly ---
    const handleUniform = () => {
        const scale = volume.scale;
        const avg = (scale.x + scale.y + scale.z) / 3;
        volume.scale.set(avg, avg, avg); // Set all scale axes to the average
        forceUpdate(); // Re-render to show the new values
    };

    let calculatedVolume = 0;
    if (!volume.sphere) {
        const scale = volume.scale;
        calculatedVolume = scale.x * scale.y * scale.z;
    } else {
        // For a sphere, radius is the scale on one axis
        const radius = volume.scale.x;
        calculatedVolume = (4 / 3) * Math.PI * Math.pow(radius, 3);
    }

    const items = [
        { label: "x", value: volume.position.x.toFixed(3), copyValue: `X:${volume.position.x.toFixed(3)}, Y:${volume.position.y.toFixed(3)}, Z:${volume.position.z.toFixed(3)}` },
        { label: "y", value: volume.position.y.toFixed(3) },
        { label: "z", value: volume.position.z.toFixed(3) },
        { label: "α", value: THREE.MathUtils.radToDeg(volume.rotation.x).toFixed(1) + '°' },
        { label: "β", value: THREE.MathUtils.radToDeg(volume.rotation.y).toFixed(1) + '°' },
        { label: "γ", value: THREE.MathUtils.radToDeg(volume.rotation.z).toFixed(1) + '°' },
        { label: t("scenePanel.properties.labels.length"), value: volume.scale.x.toFixed(2) },
        { label: t("scenePanel.properties.labels.width"), value: volume.scale.y.toFixed(2) },
        { label: t("scenePanel.properties.labels.height"), value: volume.scale.z.toFixed(2) },
    ];

    return (
        <>
            <PropertyGrid items={items} columns={3} />
            <div className="prop-summary">
                <span className="prop-summary-label">Volume:</span>
                <span className="prop-summary-value">{calculatedVolume.toLocaleString(undefined, { maximumFractionDigits: 2 })} m³</span>
            </div>
            <div className="prop-actions">
                {!volume.sphere && 
                    <div className="prop-checkbox">
                        <input type="checkbox" id="clip_volume" checked={clip} onChange={handleClipChange} />
                        <label htmlFor="clip_volume">{t("scenePanel.properties.actions.makeClipVolume")}</label>
                    </div>
                }
                <div className="prop-buttons">
                    <button onClick={handleReset}>{t("scenePanel.properties.actions.resetOrientation")}</button>
                    <button onClick={handleUniform}>{t("scenePanel.properties.actions.makeUniform")}</button>
                </div>
            </div>
        </>
    );
};

const AnnotationProperties = ({ annotation, onForceUpdate, t }) => {
    const [title, setTitle] = React.useState(annotation.title);
    const [description, setDescription] = React.useState(annotation.description);

    // This effect syncs our local state if the selected annotation changes
    React.useEffect(() => {
        setTitle(annotation.title);
        setDescription(annotation.description);
    }, [annotation]);

    const handleTitleChange = (e) => {
        setTitle(e.target.value);
        annotation.title = e.target.value;
        if (onForceUpdate) onForceUpdate(); // Tell the parent to re-render everything
    };

    const handleDescriptionChange = (e) => {
        setDescription(e.target.value);
        annotation.description = e.target.value;
        // No need to force update for description, as it's not shown in the outliner
    };

    return (
        <div className="prop-list-vertical">
            <div className="prop-row-vertical">
                <label htmlFor="anno_title">{t("scenePanel.properties.labels.title")}</label>
                <input id="anno_title" type="text" value={title} onChange={handleTitleChange} />
            </div>
            <div className="prop-row-vertical">
                <label htmlFor="anno_desc">{t("scenePanel.properties.labels.description")}</label>
                <textarea id="anno_desc" value={description} onChange={handleDescriptionChange} rows={3}></textarea>
            </div>
        </div>
    );
};

const CameraProperties = ({ camera, t }) => {
    // 1. Separate data into grid and summary items
    const gridItems = [
        { label: "x", value: camera.position.x.toFixed(2), copyValue: `X:${camera.position.x.toFixed(2)}, Y:${camera.position.y.toFixed(2)}, Z:${camera.position.z.toFixed(2)}` },
        { label: "y", value: camera.position.y.toFixed(2) },
        { label: "z", value: camera.position.z.toFixed(2) },
    ];

    const summaryItems = [
        { label: t("properties.fieldOfView"), value: camera.fov.toFixed(0) + '°' }
    ];

    // 2. Render both sections, just like in MeasureProperties
    return (
        <>
            <PropertyGrid items={gridItems} columns={3} />

            <div className="prop-summary-list">
                {summaryItems.map(item => (
                    <PropertySummary key={item.label} label={item.label} value={item.value} />
                ))}
            </div>
        </>
    );
};

const ProfileProperties = ({ profile, viewer, t }) => {
    // --- FIX #1: Add the useEffect for live updates ---
    const forceUpdate = React.useReducer(x => x + 1, 0)[1];

    useEffect(() => {
        const onUpdate = () => forceUpdate();

        // Listen for both marker movement and width changes
        profile.addEventListener('marker_moved', onUpdate);
        profile.addEventListener('width_changed', onUpdate);

        return () => {
            profile.removeEventListener('marker_moved', onUpdate);
            profile.removeEventListener('width_changed', onUpdate);
        };
    }, [profile]);

    const handleWidthChange = (newWidth) => {
        profile.setWidth(newWidth);
    };

    const handleShowProfileWindow = () => {
        console.log("Attempting to show 2D profile window for profile:", profile.name); // The requested log

        if (viewer && viewer.profileWindow && viewer.profileWindowController) {
            // 1. Tell the controller WHICH profile to show. This is critical.
            viewer.profileWindowController.setProfile(profile);

            // 2. Command the window to appear.
            viewer.profileWindow.show();
        } else {
            console.error("Profile window components are not available on the viewer object.");
        }
    };

    // Prepare items for the grid, including headers
    const gridItems = [
        { label: 'x', value: null, isHeader: true },
        { label: 'y', value: null, isHeader: true },
        { label: 'z', value: null, isHeader: true },
    ];
    profile.points.forEach(p => {
        gridItems.push({ label: null, value: p.x.toFixed(3), copyValue: `X:${p.x.toFixed(3)}, Y:${p.y.toFixed(3)}, Z:${p.z.toFixed(3)}` });
        gridItems.push({ label: null, value: p.y.toFixed(3) });
        gridItems.push({ label: null, value: p.z.toFixed(3) });
    });

    return (
        <>
            <PropertyGrid items={gridItems} columns={3} hasHeaders={true} />
            <div className="prop-summary-list">
                <NumberInput label={t("scenePanel.properties.labels.width")} value={profile.width} onChange={handleWidthChange} />
            </div>
            {/* --- NEW: Add a dedicated actions section for our button --- */}
            <div className="prop-actions">
                <div className="prop-buttons">
                    <button onClick={handleShowProfileWindow}>{t("scenePanel.properties.actions.show2DProfile")}</button>
                </div>
            </div>
        </>
    );
};

// --- Main Panel ---
export default function PropertiesPanel({ selectedObject, viewer, onForceUpdate, t }) {
    console.log('Selected: ', selectedObject);
    if (!selectedObject) {
        return <div className="properties-panel empty">{t("scenePanel.properties.empty")}</div>;
    }

    const renderDetails = () => {
        console.log('constructor name: ' + selectedObject.constructor.name);
        switch (selectedObject.constructor.name) {
            case 'PointCloudOctree':
                return <PointCloudProperties pointcloud={selectedObject} t={t} />;
            case 'BoxVolume':
            case 'SphereVolume':
                return <VolumeProperties volume={selectedObject} viewer={viewer} t={t} />;
            case 'Measure':
                return <MeasureProperties measure={selectedObject} t={t} />;
            case 'Profile':
                return <ProfileProperties profile={selectedObject} viewer={viewer} t={t} />;
            case 'Annotation':
                return <AnnotationProperties annotation={selectedObject} onForceUpdate={onForceUpdate} t={t} />;
            case 'PerspectiveCamera':
                return <CameraProperties camera={selectedObject} />;
            default:
                return <div className="prop-row">{t("properties.none")}</div>;
        }
    };

    return (
        <div className="properties-panel">
            <h4>{t("scenePanel.properties.header")}</h4>
            {renderDetails()}
        </div>
    );
}